import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, DollarSign, TrendingUp, Calendar, Download,
  Loader2, ChevronRight, Filter, X, Layers, Film, Search,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  1: 'Stage 1 · Script',
  2: 'Stage 2 · Shot Breakdown',
  2.5: 'Stage 2.5 · Crew Pre-Pro',
  3: 'Stage 3 · Asset Bible',
  4: 'Stage 4 · Sketches',
  5: 'Stage 5 · Keyframes',
  6: 'Stage 6 · DOP Spec',
  7: 'Stage 7 · Animatic',
  8: 'Stage 8 · I2V',
  9: 'Stage 9 · VLM QC',
  10: 'Stage 10 · Editorial',
  11: 'Stage 11 · Audio',
  12: 'Stage 12 · S-Log3',
  13: 'Stage 13 · Final',
};

const PROVIDER_COLORS = {
  hypereal: '#F59E0B',
  openai: '#10B981',
  anthropic: '#8B5CF6',
  gemini: '#0EA5E9',
  veo: '#EC4899',
  kling: '#F97316',
  higgsfield: '#EF4444',
  midjourney: '#6366F1',
  untagged: '#6B7280',
};

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const usd4 = (n) => `$${(Number(n) || 0).toFixed(4)}`;

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function StatCard({ label, value, sublabel, icon: Icon, accent = '#0EA5E9', testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur p-4 transition hover:bg-white/[0.05]"
      style={{ borderTopColor: accent, borderTopWidth: 2 }}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-white/50">{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
      </div>
      <div className="text-2xl font-semibold text-white">{value}</div>
      {sublabel && <div className="text-xs text-white/40 mt-1">{sublabel}</div>}
    </div>
  );
}

function ProviderPill({ provider }) {
  const color = PROVIDER_COLORS[provider] || PROVIDER_COLORS.untagged;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-medium"
      style={{ background: `${color}22`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {provider || 'untagged'}
    </span>
  );
}

function SpendOverviewTab({ summary, byProject, byOperation, onPickProject, loading }) {
  return (
    <div className="space-y-6">
      {/* Header widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Today"
          value={usd(summary?.today?.total_usd)}
          sublabel={`${summary?.today?.call_count || 0} API fires`}
          icon={DollarSign}
          accent="#0EA5E9"
          testId="spend-stat-today"
        />
        <StatCard
          label="This week"
          value={usd(summary?.week?.total_usd)}
          sublabel={`${summary?.week?.call_count || 0} API fires`}
          icon={TrendingUp}
          accent="#10B981"
          testId="spend-stat-week"
        />
        <StatCard
          label="This month"
          value={usd(summary?.month?.total_usd)}
          sublabel={`${summary?.month?.call_count || 0} API fires`}
          icon={Calendar}
          accent="#F59E0B"
          testId="spend-stat-month"
        />
        <div
          data-testid="spend-by-provider"
          className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
          style={{ borderTopColor: '#8B5CF6', borderTopWidth: 2 }}
        >
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Today by provider</div>
          {(summary?.by_provider_today || []).length === 0 ? (
            <div className="text-sm text-white/40">No spend yet today</div>
          ) : (
            <div className="space-y-1.5">
              {(summary?.by_provider_today || []).slice(0, 4).map((p) => (
                <div key={p.provider} className="flex items-center justify-between text-xs">
                  <ProviderPill provider={p.provider} />
                  <span className="text-white/80 font-mono">{usd(p.total_usd)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Two columns: top projects + top operations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="spend-top-projects">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Top projects · last 7 days</h3>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" />}
          </div>
          <div className="divide-y divide-white/5">
            {(byProject?.projects || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-white/40">No spend in window</div>
            ) : (
              (byProject?.projects || []).slice(0, 10).map((p) => (
                <button
                  key={p.project_id}
                  onClick={() => onPickProject(p.project_id)}
                  data-testid={`spend-project-row-${p.project_id}`}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-white/5 transition text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-xs text-white/40 flex items-center gap-2">
                      <span>{p.calls} calls</span>
                      <span>·</span>
                      <span>{timeAgo(p.last_activity)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-mono text-white">{usd(p.total_usd)}</span>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="spend-top-operations">
          <div className="px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-white">Top operations · last 7 days</h3>
          </div>
          <div className="divide-y divide-white/5">
            {(byOperation?.operations || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-white/40">No data</div>
            ) : (
              (byOperation?.operations || []).slice(0, 10).map((op) => (
                <div key={op.operation} className="px-4 py-2.5 flex items-center justify-between">
                  <div className="min-w-0 flex-1 flex items-center gap-2">
                    <ProviderPill provider={op.provider} />
                    <span className="text-sm text-white/80 truncate font-mono">{op.operation}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-white/40">{op.calls}×</span>
                    <span className="text-sm font-mono text-white">{usd(op.total_usd)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Operator-requested 8-column API-fire table — Rohit 2026-05-26 */}
      <ApiFiresTable projects={byProject?.projects || []} />
    </div>
  );
}


// ───────────────────────────────────────────────────────────────
// API Fires Table — 8 cols per Rohit's 2026-05-26 spec:
// TIMESTAMP IN IST | PROJ | STAGE | API PROVIDER | API ID | MODEL |
// API TRIGGER (BUTTON CLICKED) | COST
// ───────────────────────────────────────────────────────────────
function ApiFiresTable({ projects }) {
  const [fires, setFires] = useState({ rows: [], row_count: 0, total_cost_usd: 0 });
  const [loading, setLoading] = useState(false);
  // `days` is the rolling window. `fromDate` (YYYY-MM-DD) overrides days
  // when set — used by the "From 1 Apr 2026" preset and the date picker.
  const [days, setDays] = useState(7);
  const [fromDate, setFromDate] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFires = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '5000' });
      if (fromDate) {
        params.set('from_date', fromDate);
      } else {
        params.set('days', String(days));
      }
      if (projectFilter) params.set('project_id', projectFilter);
      const { data } = await axios.get(
        `${API}/api/admin/spend-fires?${params.toString()}`,
        { headers }
      );
      setFires(data);
    } catch (e) {
      console.error('[ApiFiresTable] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [headers, days, fromDate, projectFilter]);

  useEffect(() => { fetchFires(); }, [fetchFires]);

  const downloadCsv = useCallback(() => {
    const params = new URLSearchParams({ limit: '200000' });
    if (fromDate) {
      params.set('from_date', fromDate);
    } else {
      params.set('days', String(days));
    }
    if (projectFilter) params.set('project_id', projectFilter);
    fetch(`${API}/api/admin/spend-fires.csv?${params.toString()}`, { headers })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const scope = projectFilter ? projectFilter.slice(0, 8) : 'all';
        const windowTag = fromDate ? `since_${fromDate}` : `last_${days}d`;
        a.download = `api_fires_${scope}_${windowTag}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  }, [headers, days, fromDate, projectFilter]);

  const filteredRows = useMemo(() => {
    const q = (search || '').toLowerCase();
    return (fires.rows || []).filter((r) => {
      if (providerFilter && r.api_provider !== providerFilter) return false;
      if (!q) return true;
      return (
        (r.project || '').toLowerCase().includes(q) ||
        (r.stage || '').toLowerCase().includes(q) ||
        (r.model || '').toLowerCase().includes(q) ||
        (r.api_trigger || '').toLowerCase().includes(q) ||
        (r.api_id || '').toLowerCase().includes(q)
      );
    });
  }, [fires.rows, search, providerFilter]);

  const uniqueProviders = useMemo(() => {
    const set = new Set();
    (fires.rows || []).forEach((r) => { if (r.api_provider) set.add(r.api_provider); });
    return Array.from(set).sort();
  }, [fires.rows]);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="api-fires-table">
      <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-white">
          API fires · per project · per call
        </h3>
        <span className="text-xs text-white/40">
          {fires.row_count} rows · {usd(fires.total_cost_usd)} total
          {loading && <Loader2 className="inline w-3 h-3 ml-2 animate-spin" />}
        </span>
        <div className="flex-1" />
        <select
          value={fromDate ? `from:${fromDate}` : `days:${days}`}
          onChange={(e) => {
            const v = e.target.value;
            if (v.startsWith('from:')) {
              setFromDate(v.slice(5));
            } else if (v === 'custom') {
              // No-op — let the date input drive
            } else {
              setFromDate('');
              setDays(parseInt(v.replace('days:', ''), 10));
            }
          }}
          data-testid="api-fires-days-select"
          className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none"
        >
          <option value="days:1">Last 24h</option>
          <option value="days:7">Last 7d</option>
          <option value="days:30">Last 30d</option>
          <option value="days:90">Last 90d</option>
          <option value="days:365">Last 365d</option>
          <option value="from:2026-04-01">From 1 Apr 2026 → today</option>
          <option value="from:2026-01-01">From 1 Jan 2026 → today</option>
          <option value="days:3650">All time</option>
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="api-fires-from-date"
          title="Custom backlog start date (overrides Last-Nd)"
          className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none w-[140px]"
        />
        {fromDate && (
          <button
            onClick={() => setFromDate('')}
            data-testid="api-fires-clear-from"
            title="Clear custom date — revert to rolling window"
            className="text-xs text-white/40 hover:text-white/80 px-1"
          >
            ✕
          </button>
        )}
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          data-testid="api-fires-project-filter"
          className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none max-w-[200px]"
        >
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.project_id} value={p.project_id}>{p.name}</option>
          ))}
        </select>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          data-testid="api-fires-provider-filter"
          className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none"
        >
          <option value="">All providers</option>
          {uniqueProviders.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40" />
          <input
            type="text"
            placeholder="Search project / model / trigger / ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="api-fires-search"
            className="bg-white/5 border border-white/10 rounded-md pl-7 pr-2 py-1 text-xs text-white w-64 focus:outline-none focus:border-white/30"
          />
        </div>
        <button
          onClick={downloadCsv}
          data-testid="api-fires-download-csv"
          className="px-3 py-1 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs flex items-center gap-1.5 border border-emerald-500/30"
        >
          <Download className="w-3.5 h-3.5" />
          Download CSV
        </button>
      </div>
      <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#0B0F19] z-10">
            <tr className="text-left text-white/50 uppercase tracking-wider">
              <th className="px-3 py-2 font-normal">Timestamp (IST)</th>
              <th className="px-3 py-2 font-normal">Project</th>
              <th className="px-3 py-2 font-normal">Stage</th>
              <th className="px-3 py-2 font-normal">API Provider</th>
              <th className="px-3 py-2 font-normal">API ID</th>
              <th className="px-3 py-2 font-normal">Model</th>
              <th className="px-3 py-2 font-normal">Trigger (button)</th>
              <th className="px-3 py-2 font-normal text-right">Cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-white/40">
                  {loading ? 'Loading…' : 'No API fires in this window'}
                </td>
              </tr>
            ) : (
              filteredRows.map((r, i) => (
                <tr key={`${r.api_id || i}-${i}`} className="hover:bg-white/[0.04]">
                  <td className="px-3 py-1.5 font-mono text-white/70 whitespace-nowrap">
                    {r.timestamp_ist || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-white truncate max-w-[180px]" title={r.project}>
                    {r.project}
                  </td>
                  <td className="px-3 py-1.5 text-white/70 whitespace-nowrap">
                    {r.stage}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.api_provider ? <ProviderPill provider={r.api_provider} /> : <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-white/40 text-[10px]" title={r.api_id}>
                    {r.api_id ? r.api_id.slice(0, 12) + '…' : '—'}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-white/70 whitespace-nowrap">
                    {r.model || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-white/80">
                    {r.api_trigger === '(untagged)' ? (
                      <span className="text-white/30 italic">(untagged)</span>
                    ) : (
                      r.api_trigger
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-white text-right">
                    ${(Number(r.cost_usd) || 0).toFixed(4)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectDeepDive({ projectId, onBack, headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ stage: '', operation: '', shot: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/admin/project-spend/${projectId}`, { headers });
      setData(d);
    } catch (e) {
      console.error('Failed to fetch project spend', e);
    } finally {
      setLoading(false);
    }
  }, [projectId, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredTimeline = useMemo(() => {
    if (!data?.timeline) return [];
    return data.timeline.filter((e) => {
      if (filter.stage && String(e.stage || 'unstaged') !== filter.stage) return false;
      if (filter.operation && e.operation !== filter.operation) return false;
      if (filter.shot && e.shot_id !== filter.shot) return false;
      if (searchTerm) {
        const hay = `${e.operation || ''} ${e.model || ''} ${e.provider || ''} ${e.shot_id || ''}`.toLowerCase();
        if (!hay.includes(searchTerm.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, filter, searchTerm]);

  const downloadCsv = () => {
    const token = localStorage.getItem('token');
    // Use fetch + blob since we need to send auth header on a download
    fetch(`${API}/api/admin/project-spend/${projectId}.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `spend_${data?.name || projectId}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-white/60" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          data-testid="spend-deepdive-back-btn"
          className="text-sm text-white/60 hover:text-white flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to overview
        </button>
        <button
          onClick={downloadCsv}
          data-testid="spend-csv-download-btn"
          className="text-xs px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-white flex items-center gap-1.5 transition"
        >
          <Download className="w-3.5 h-3.5" />
          Download CSV
        </button>
      </div>

      {/* Project header */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <h2 className="text-xl font-semibold text-white mb-1">{data.name}</h2>
        <div className="flex items-center gap-4 text-xs text-white/50">
          <span>Project ID: <span className="font-mono">{data.project_id}</span></span>
          <span>·</span>
          <span>Current stage: {data.current_stage ?? '—'}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <div className="text-xs uppercase text-white/40">Lifetime spend</div>
            <div className="text-xl font-mono text-white">{usd(data.total_cost_usd)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-white/40">Total API fires</div>
            <div className="text-xl font-mono text-white">{data.total_calls}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-white/40">Stages with spend</div>
            <div className="text-xl font-mono text-white">{data.stage_rollup?.length || 0}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-white/40">Shots with spend</div>
            <div className="text-xl font-mono text-white">{data.shot_rollup?.length || 0}</div>
          </div>
        </div>
      </div>

      {/* Stage + Operation rollups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="spend-stage-rollup">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Layers className="w-4 h-4 text-white/60" />
            <h3 className="text-sm font-semibold text-white">By stage</h3>
          </div>
          <div className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
            {(data.stage_rollup || []).map((s) => (
              <button
                key={s.stage}
                onClick={() => setFilter((f) => ({ ...f, stage: String(s.stage) }))}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-white/5 text-left text-sm"
              >
                <span className="text-white/80">{STAGE_LABELS[s.stage] || `Stage ${s.stage}`}</span>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-white/40">{s.calls}×</span>
                  <span className="font-mono text-white">{usd(s.total_usd)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="spend-operation-rollup">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-white/60" />
            <h3 className="text-sm font-semibold text-white">By operation</h3>
          </div>
          <div className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
            {(data.operation_rollup || []).map((op) => (
              <button
                key={op.operation}
                onClick={() => setFilter((f) => ({ ...f, operation: op.operation }))}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-white/5 text-left"
              >
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <ProviderPill provider={op.provider} />
                  <span className="text-sm text-white/80 truncate font-mono">{op.operation}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-white/40">{op.calls}×</span>
                  <span className="font-mono text-white">{usd(op.total_usd)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Shot rollup */}
      {(data.shot_rollup || []).length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="spend-shot-rollup">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Film className="w-4 h-4 text-white/60" />
            <h3 className="text-sm font-semibold text-white">Top 12 shots by spend</h3>
          </div>
          <div className="divide-y divide-white/5">
            {(data.shot_rollup || []).slice(0, 12).map((s) => (
              <button
                key={s.shot_id}
                onClick={() => setFilter((f) => ({ ...f, shot: s.shot_id }))}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-white/5 text-left text-sm"
              >
                <span className="text-white/60 font-mono text-xs truncate">{s.shot_id}</span>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-white/40">{s.calls}×</span>
                  <span className="font-mono text-white">{usd(s.total_usd)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline with filters */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="spend-timeline">
        <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/60" />
            Timeline ({filteredTimeline.length} / {data.timeline?.length || 0})
          </h3>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-3.5 h-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="spend-timeline-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search operation / model / shot…"
              className="w-full pl-8 pr-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white text-xs placeholder-white/30 focus:outline-none focus:border-white/30"
            />
          </div>
          {(filter.stage || filter.operation || filter.shot) && (
            <button
              onClick={() => setFilter({ stage: '', operation: '', shot: '' })}
              data-testid="spend-clear-filters-btn"
              className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/15 text-white/80 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          )}
        </div>
        {(filter.stage || filter.operation || filter.shot) && (
          <div className="px-4 py-2 bg-white/[0.02] border-b border-white/10 text-xs text-white/60 flex flex-wrap items-center gap-2">
            <span>Active filters:</span>
            {filter.stage && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">stage={filter.stage}</span>}
            {filter.operation && <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded">op={filter.operation}</span>}
            {filter.shot && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded font-mono truncate max-w-[200px]">shot={filter.shot.slice(0, 12)}…</span>}
          </div>
        )}
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.04] sticky top-0">
              <tr className="text-left text-white/50 uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Operation</th>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Shot</th>
                <th className="px-3 py-2 font-medium text-right">Tokens (in/out)</th>
                <th className="px-3 py-2 font-medium text-right">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredTimeline.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-white/40">No matches</td>
                </tr>
              ) : (
                filteredTimeline.map((e, idx) => (
                  <tr key={idx} className="hover:bg-white/5 transition">
                    <td className="px-3 py-1.5 text-white/60 whitespace-nowrap" title={e.timestamp}>{timeAgo(e.timestamp)}</td>
                    <td className="px-3 py-1.5 text-white/70">{e.stage ?? '—'}</td>
                    <td className="px-3 py-1.5 text-white/90 font-mono">{e.operation}</td>
                    <td className="px-3 py-1.5"><ProviderPill provider={e.provider || 'untagged'} /></td>
                    <td className="px-3 py-1.5 text-white/60 font-mono">{e.model || '—'}</td>
                    <td className="px-3 py-1.5 text-white/40 font-mono truncate max-w-[120px]" title={e.shot_id}>
                      {e.shot_id ? `${e.shot_id.slice(0, 8)}…` : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right text-white/60 font-mono">
                      {e.tokens_in || e.tokens_out ? `${e.tokens_in || 0}/${e.tokens_out || 0}` : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right text-white font-mono font-semibold">{usd4(e.total_cost)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminSpendPanel() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [byProject, setByProject] = useState(null);
  const [byOperation, setByOperation] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, o] = await Promise.all([
        axios.get(`${API}/api/admin/spend-summary`, { headers }),
        axios.get(`${API}/api/admin/spend-by-project?days=${days}&limit=50`, { headers }),
        axios.get(`${API}/api/admin/spend-by-operation?days=${days}`, { headers }),
      ]);
      setSummary(s.data);
      setByProject(p.data);
      setByOperation(o.data);
    } catch (e) {
      console.error('Failed to fetch spend data', e);
    } finally {
      setLoading(false);
    }
  }, [headers, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              data-testid="spend-back-to-dashboard-btn"
              className="text-white/60 hover:text-white flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <span className="text-white/20">/</span>
            <h1 className="text-lg font-semibold">Spend & Cost Audit</h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="spend-window-select"
              className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <button
              onClick={fetchAll}
              disabled={loading}
              data-testid="spend-refresh-btn"
              className="px-3 py-1 rounded-md bg-white/10 hover:bg-white/15 text-xs text-white flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {selectedProject ? (
          <ProjectDeepDive
            projectId={selectedProject}
            onBack={() => setSelectedProject(null)}
            headers={headers}
          />
        ) : (
          <SpendOverviewTab
            summary={summary}
            byProject={byProject}
            byOperation={byOperation}
            onPickProject={setSelectedProject}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}
